import { createFileRoute, Link } from "@tanstack/react-router";
import { Leaf, Coffee, Car, Phone } from "lucide-react";
import { breadcrumbSchema, canonical, faqPageSchema } from "@/lib/seo";

export const Route = createFileRoute("/blog/fall-getaway-crystal-beach-niagara")({
  head: () => ({
    meta: [
      { title: "Fall Getaways in Crystal Beach and Niagara - Malak BnB Resort" },
      { name: "description", content: "A quieter side of Crystal Beach and the Niagara Region in fall. Fewer crowds, cooler air, and a cozy stay at Malak BnB Resort." },
      { property: "og:title", content: "Fall Getaways in Crystal Beach and Niagara - Malak BnB Resort" },
      { property: "og:description", content: "Crystal Beach empties out and slows down in fall. Here is why that makes it worth visiting." },
      { property: "og:url", content: "https://malakbnbresort.ca/blog/fall-getaway-crystal-beach-niagara" },
      { property: "og:image", content: "/images/niagara-falls-autumn.webp" },
      { "script:ld+json": faqPageSchema(FAQ) },
      {
        "script:ld+json": breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "Blog", path: "/blog" },
          { name: "Fall Getaways in Crystal Beach and Niagara", path: "/blog/fall-getaway-crystal-beach-niagara" },
        ]),
      },
    ],
    links: [canonical("/blog/fall-getaway-crystal-beach-niagara")],
  }),
  component: FallGetawayPage,
});

const FAQ = [
  {
    q: "Is Crystal Beach worth visiting in the fall?",
    a: "Yes. Fall brings cooler air, quieter streets, and far fewer visitors than the summer months. Bay Beach is a good place for a walk rather than a swim, and the whole town takes on a slower, more residential feel that summer crowds do not allow.",
  },
  {
    q: "Is Niagara Falls worth visiting in the fall?",
    a: "Fall is one of the better times to see Niagara Falls, with shorter lines, easier parking, and cooler weather for walking the Niagara Parkway. Malak BnB Resort is about a 30 minute drive from the falls, making it a comfortable base for a fall day trip.",
  },
  {
    q: "Is breakfast still included at Malak BnB Resort in the fall?",
    a: "Yes. A continental breakfast buffet is included with every stay at Malak BnB Resort year round, a good start to a cooler fall morning before heading out.",
  },
  {
    q: "How do I request a fall stay at Malak BnB Resort?",
    a: "Submit a reservation request through the Malak BnB Resort booking form with your preferred dates. The team personally reviews and confirms availability before sending payment details.",
  },
];

function FallGetawayPage() {
  return (
    <div>
      {/* Hero */}
      <section className="relative h-[55vh] min-h-[380px] overflow-hidden">
        <img
          src="/images/niagara-falls-autumn.webp"
          alt="Niagara Falls surrounded by autumn foliage"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground/65 via-foreground/20 to-transparent" />
        <div className="relative z-10 flex h-full items-end pb-16 px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-7xl w-full">
            <div className="flex items-center gap-2 text-white/70 text-sm mb-3">
              <Link to="/blog" className="hover:text-white transition-colors">Blog</Link>
              <span>/</span>
              <span>Fall Getaways</span>
            </div>
            <p className="text-white/70 font-semibold text-sm uppercase tracking-widest mb-3">Lake Erie and Niagara Region</p>
            <h1 className="text-4xl sm:text-5xl font-heading text-white leading-tight max-w-2xl">
              Fall Getaways in Crystal Beach and Niagara
            </h1>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="py-20">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg text-muted-foreground space-y-5 leading-relaxed">
            <p>
              Crystal Beach in the fall is a different town from the one you find in July. The summer crowd has
              gone home, the streets are quiet again, and the lake takes on a colder, more dramatic character. It
              is not everyone's version of a getaway, but for the right traveller, it is the better version.
            </p>
            <p>
              Malak BnB Resort stays open through the season, breakfast included and rooms just as comfortable in
              October as they are in July, only with a lot more room to yourself.
            </p>
          </div>
          <p className="mt-3 text-xs text-muted-foreground">
            Photo by Ali Soheil via{" "}
            <a href="https://www.pexels.com/photo/breathtaking-view-of-niagara-falls-in-autumn-34911227/" target="_blank" rel="noopener noreferrer" className="underline hover:text-foreground">
              Pexels
            </a>
          </p>
        </div>
      </section>

      {/* The quiet town */}
      <section className="pb-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="rounded-2xl overflow-hidden shadow-xl order-2 lg:order-1">
              <img src="/images/explore-ridge-beach.webp" alt="Ridge Beach shoreline near Crystal Beach in the off season" className="w-full object-cover aspect-[4/3]" loading="lazy" />
            </div>
            <div className="order-1 lg:order-2">
              <div className="flex items-center gap-2 mb-3">
                <Leaf className="h-5 w-5 text-primary" />
                <p className="text-primary font-semibold text-sm uppercase tracking-wider">The Town in Fall</p>
              </div>
              <h2 className="text-2xl sm:text-3xl font-heading text-foreground leading-snug mb-4">
                A Quieter Version of Crystal Beach
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                Bay Beach empties out once the water cools, and the shoreline becomes a place for a walk instead of
                a swim. The town settles into its residential rhythm again, and the handful of restaurants and cafes
                that stay open through fall feel less like tourist stops and more like the local spots they actually
                are.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Niagara in fall */}
      <section className="py-20 bg-card">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-primary font-semibold text-sm uppercase tracking-wider mb-3">A Day Trip Worth Taking</p>
          <h2 className="text-3xl font-heading text-foreground mb-6">Niagara Falls and Wine Country in Fall</h2>
          <p className="text-muted-foreground leading-relaxed">
            Fall is one of the better times to see Niagara Falls. The lines are shorter, parking is easier, and the
            Niagara Parkway is a genuinely nice drive with the leaves turning along the river. The wider Niagara
            wine country also comes into its own in fall, worth building into a day trip from Crystal Beach if you
            have the time.
          </p>
        </div>
      </section>

      {/* Stay */}
      <section className="py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div className="rounded-2xl overflow-hidden shadow-xl">
              <img src="/images/gallery-bedroom-warm.webp" alt="Warmly lit bedroom at Malak BnB Resort" className="w-full object-cover aspect-[4/3]" loading="lazy" />
            </div>
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Coffee className="h-5 w-5 text-primary" />
                <p className="text-primary font-semibold text-sm uppercase tracking-wider">Where to Stay</p>
              </div>
              <h2 className="text-3xl font-heading text-foreground leading-snug mb-6">
                A Warm Room and Breakfast Waiting
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-8">
                Malak BnB Resort keeps the same warmth in fall that it has all year: continental breakfast every
                morning, comfortable rooms, and a five minute walk to Bay Beach when you want the air rather than
                the water. It is a good base for a fall Niagara day trip, and a good place to simply slow down for
                a weekend.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/book" className="inline-flex items-center justify-center rounded-full bg-primary px-8 py-3 text-sm font-semibold text-primary-foreground hover:bg-primary/90 transition-colors">
                  Request to Book
                </Link>
                <Link to="/rooms" className="inline-flex items-center justify-center rounded-full border border-border px-8 py-3 text-sm font-semibold text-foreground hover:bg-muted transition-colors">
                  View Rooms and Rates
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related */}
      <section className="pb-4">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 text-center">
          <Link to="/blog/niagara-falls-day-trip-from-crystal-beach" className="text-primary font-semibold text-sm hover:underline">
            Read the full guide to a Niagara Falls day trip →
          </Link>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-primary font-semibold text-sm uppercase tracking-wider mb-3">Common Questions</p>
            <h2 className="text-3xl font-heading text-foreground">Planning a Fall Trip</h2>
          </div>
          <div className="space-y-8">
            {FAQ.map(({ q, a }) => (
              <div key={q} className="border-b border-border pb-8 last:border-0 last:pb-0">
                <h3 className="font-heading text-lg text-foreground mb-3">{q}</h3>
                <p className="text-muted-foreground leading-relaxed text-sm">{a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-foreground">
        <div className="mx-auto max-w-3xl px-4 text-center">
          <p className="text-primary-foreground/50 font-semibold text-sm uppercase tracking-widest mb-4">Crystal Beach, Ontario</p>
          <h2 className="text-3xl sm:text-4xl font-heading text-primary-foreground leading-tight">
            Request a fall stay at Malak BnB Resort
          </h2>
          <p className="mt-5 text-primary-foreground/70 max-w-lg mx-auto leading-relaxed">
            Submit a reservation request and the team will personally confirm availability and send payment details directly.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link to="/book" className="inline-flex items-center justify-center rounded-full bg-primary px-10 py-4 text-sm font-semibold text-primary-foreground hover:bg-primary/90 transition-colors shadow-lg">
              Request Your Stay
            </Link>
            <a href="tel:+12899298055" className="inline-flex items-center gap-2 text-primary-foreground/70 hover:text-primary-foreground text-sm font-medium transition-colors">
              <Phone className="h-4 w-4" />
              1-289-929-8055
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
